import { Autocomplete } from '@material-ui/lab';
import { Controller, FieldPath, FieldValues, useFormContext } from 'react-hook-form';

import { Font } from '@/components/shared/components/Font';
import { useAutocompleteFields } from '@/components/shared/components/NewContextForms/hooks/useAutocompleteField';
import { useCustomFieldStyles } from '@/components/shared/components/NewContextForms/hooks/useCustomFieldStyles';
import {
  CustomTextField,
  CustomTextFieldProps,
} from '@/components/shared/components/NewContextForms/shared/CustomTextField';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';

export interface AutocompleteFormFieldProps<TFieldValues extends FieldValues, TOptionObject>
  extends CustomTextFieldProps<TFieldValues> {
  name: FieldPath<TFieldValues>;
  displayFn: (option: TOptionObject) => string;
  findFn: (searchValue: string) => TOptionObject[];
  initialValueFn?: (selectedOption: TOptionObject) => TOptionObject;
  searchValueMinLength?: number;
}

export const AutocompleteFormField = <
  TFieldValues extends FieldValues,
  TOptionObject extends object | null | undefined,
>({
  name,
  displayFn,
  findFn,
  initialValueFn = (selectedOption) => selectedOption,
  searchValueMinLength = 1,
  ...props
}: AutocompleteFormFieldProps<TFieldValues, TOptionObject>) => {
  const { control } = useFormContext();
  const { optionList, setSearchValue } = useAutocompleteFields<TOptionObject>(findFn, searchValueMinLength);
  const { getMUIAutocompleteFormClasses } = useCustomFieldStyles({
    fieldVariant: props.fieldVariant,
    disabled: props.disabled,
  });

  const autocompleteClasses = getMUIAutocompleteFormClasses();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        return (
          <>
            <Autocomplete
              classes={autocompleteClasses}
              freeSolo
              clearOnBlur
              selectOnFocus
              options={optionList}
              value={initialValueFn(field.value)}
              onChange={(_, newValue) => {
                if (typeof newValue !== 'object') return;
                field.onChange(newValue);
              }}
              onInputChange={(_, data) => {
                setSearchValue(data);
              }}
              getOptionLabel={(option) => displayFn(option)}
              renderOption={(option) =>
                props.fieldVariant === FieldVariant.form ? (
                  <Font variant="body2">{displayFn(option)}</Font>
                ) : (
                  displayFn(option)
                )
              }
              renderInput={(params) => {
                return <CustomTextField {...params} {...props} error={error} />;
              }}
              disabled={props.disabled}
            />
          </>
        );
      }}
    />
  );
};
